"use client";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import Image from "next/image";
import { useRef, type ReactNode, type RefObject } from "react";

import { resolvePageImage, type SitePageImageMap } from "@/lib/site-page-images";

gsap.registerPlugin(ScrollTrigger);

const accent = "text-[#ff5c00]";

type TeamMember = {
  name: string;
  role: string;
  traits: string;
};

const TEAM_MEMBERS: TeamMember[] = [
  {
    name: "Maneka",
    role: "Digital Marketer",
    traits: "Algorithm Master - Fitness Freak - Fashion Conscious - Early Riser",
  },
  {
    name: "Edward",
    role: "Full-Stack Senior Developer",
    traits: "Bug Hunter - Coffee-Powered Problem Solver - Dad First, Nerd Second",
  },
  {
    name: "Kirsten",
    role: "Google Ads Lead",
    traits: "Mother to two - Data Perfectionist - Undeniably the family linchpin",
  },
  {
    name: "Gary",
    role: "Design Lead",
    traits: "Dog loving petrol head - delivered with dry wit",
  },
];

function TeamCard({
  title,
  footer,
  children,
}: {
  title: string;
  footer: string;
  children: ReactNode;
}) {
  return (
    <article className="flex h-full min-h-0 flex-col overflow-y-auto overscroll-contain rounded-[1.75rem] bg-white p-6 shadow-sm sm:p-8 md:rounded-4xl md:p-10 lg:rounded-[2.25rem]">
      <p
        className={`text-2xl font-medium leading-none sm:text-3xl md:text-4xl ${accent}`}
        aria-hidden
      >
        *
      </p>
      <h3
        className={`mt-4 text-xl font-semibold tracking-tight sm:mt-6 sm:text-2xl md:text-3xl ${accent}`}
      >
        {title}
      </h3>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-[#231f20]/90 sm:mt-4 sm:text-base md:text-lg">
        {children}
      </p>
      <div className="mt-4 text-sm font-medium text-[#231f20]/50 sm:mt-6 sm:text-base md:mt-8 md:text-lg lg:mt-auto lg:pt-6">
        {footer}
      </div>
    </article>
  );
}

function ImageCard({
  src,
  alt,
  priority,
  slideRef,
}: {
  src: string;
  alt: string;
  priority?: boolean;
  slideRef?: RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      ref={slideRef}
      className="relative h-full min-h-0 w-full overflow-hidden rounded-[1.75rem] bg-[#f5f2f0] will-change-transform md:rounded-4xl lg:rounded-[2.25rem]"
    >
      <div className="relative h-full min-h-0 w-full">
        <Image
          src={src}
          alt={alt}
          fill
          priority={priority}
          className="object-cover"
          sizes="(max-width: 1023px) 100vw, 33vw"
        />
      </div>
    </div>
  );
}

export function ManageTeamSection({
  pageImageMap,
}: {
  pageImageMap?: SitePageImageMap;
}) {
  const manageTeamImg = resolvePageImage("who-we-are-manage-team", pageImageMap);

  const scope = useRef<HTMLElement | null>(null);
  const rowTriggerRef = useRef<HTMLDivElement | null>(null);
  const slideRef = useRef<HTMLDivElement | null>(null);

  useGSAP(
    () => {
      const rowTrigger = rowTriggerRef.current;
      const slide = slideRef.current;
      if (!rowTrigger || !slide) return;

      const mm = gsap.matchMedia();

      mm.add(
        {
          reduceMotion: "(prefers-reduced-motion: reduce)",
          isDesktop: "(min-width: 1024px)",
        },
        (context) => {
          const { reduceMotion, isDesktop } = context?.conditions as {
            reduceMotion: boolean;
            isDesktop: boolean;
          };

          if (!isDesktop || reduceMotion) {
            gsap.set(slide, { xPercent: 0 });
            return;
          }

          gsap.fromTo(
            slide,
            { xPercent: 109 },
            {
              xPercent: 0,
              ease: "none",
              scrollTrigger: {
                trigger: rowTrigger,
                start: "top 88%",
                end: "top 60%",
                scrub: 1.15,
                invalidateOnRefresh: true,
              },
            },
          );
        },
      );

      return () => {
        mm.revert();
      };
    },
    { scope, dependencies: [] },
  );

  return (
    <section
      id="want-a-cool-job"
      ref={scope}
      className="relative scroll-mt-20 bg-[#b5adaa] pt-16 pb-20 font-sans text-[#231f20] md:scroll-mt-24 md:pt-20 md:pb-28"
      aria-labelledby="manage-team-heading"
    >
      <div className="site-content">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-16 xl:gap-20">
          <div className="max-w-[650px] lg:col-span-7">
            <p className="text-sm font-medium tracking-wide text-[#231f20]/80 md:text-base">
              Managing the team
            </p>
            <h2
              id="manage-team-heading"
              className="mt-4 text-4xl leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl xl:text-7xl"
            >
              Leading the path
              with innovation,
              creativity and above
              all
              <span className={accent}> growth</span>
            </h2>
          </div>

          <div className="lg:col-span-5">
            <p
              className={`text-4xl font-medium leading-none md:text-5xl ${accent}`}
              aria-hidden
            >
              *
            </p>
            <hr className="mt-4 max-w-full border-0 border-t border-[#231f20]/85" />
            <p className="mt-6 text-base leading-relaxed text-[#231f20]/95 md:text-lg">
              With a small dedicated team of highly skilled digital marketers,
              creatives, and developers, Hi Ho focuses on finding the optimal
              way to transform your websites and digital channels into powerful
              engines for growth – driving traffic, engagement, and sales.
            </p>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-10 md:mt-20 md:gap-12 lg:grid lg:grid-cols-3 lg:items-start lg:gap-x-8 lg:gap-y-10 lg:overflow-visible lg:min-h-[clamp(26rem,34vw,40rem)] xl:gap-x-10 xl:gap-y-10 xl:min-h-[clamp(28rem,32vw,42rem)]">
          <div className="flex w-full flex-col gap-4 sm:gap-5 lg:contents">
            <div
              ref={rowTriggerRef}
              className="aspect-square w-full shrink-0 lg:relative lg:z-10 lg:col-start-2 lg:row-start-1 lg:aspect-auto lg:h-full lg:min-h-0 lg:w-auto lg:max-w-none"
            >
              <ImageCard
                slideRef={slideRef}
                priority
                src={manageTeamImg.src}
                alt={manageTeamImg.alt}
              />
            </div>

            <div className="aspect-square w-full shrink-0 lg:relative lg:z-30 lg:col-start-3 lg:row-start-1 lg:aspect-auto lg:h-full lg:min-h-0 lg:w-auto lg:max-w-none lg:-translate-x-[14%] xl:translate-x-0">
              <TeamCard title="Christopher" footer="Creative / Managing Director">
                Christopher leads Hi Ho’s forward-thinking digital team with the
                belief that every client is different—and so is their digital
                ecosystem. At Hi Ho, the focus is designing the right mix of
                strategy, technology, and creativity to build digital platforms
                and marketing systems that drive engagement, impact, and
                measurable growth.
              </TeamCard>
            </div>
          </div>
        </div>

        <div className="mt-16 md:mt-20">
          <h3 className={`text-5xl font-medium tracking-tight ${accent}`}>
            The Team
          </h3>
          <div className="mt-6 border-t border-[#231f20]/55">
            {TEAM_MEMBERS.map((member) => (
              <div
                key={member.name}
                className="grid grid-cols-1 gap-3 border-b border-[#231f20]/55 py-5 md:grid-cols-12 md:items-center md:gap-6"
              >
                <p className="text-3xl leading-none tracking-tight text-[#231f20]/60 md:col-span-3 md:text-4xl">
                  {member.name}
                </p>
                <p className="text-sm font-semibold uppercase tracking-wide text-[#231f20]/50 md:col-span-4 md:text-base">
                  {member.role}
                </p>
                <p className="text-sm font-semibold uppercase tracking-wide text-[#ff5c00]/80 md:col-span-5 md:text-base">
                  {member.traits}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
